import React, { useCallback, useState } from 'react';
import { colors } from '@alixpartners/ui-components';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighlightBlock from './HighlightBlock';
import {
	ChartWrapper,
	ColorWrapper,
	ColorSymbol,
	TooltipGrid,
	TooltipWrapper,
	TooltipRowTitle,
	TooltipRowValue,
	SummaryBackgroundWrapper,
	SummaryTriangle,
	Wrapper,
} from './WaterfallChart.styles';
import { renderToString } from 'react-dom/server';
import { getFormattedSpendWithSymbol } from 'utils/number';
import { CHART_TYPES, CHART_TYPE_MAP, makeUnknownFromEmpty } from '../utils';

const STACKED_LABEL_HEIGHT = 14;

const ChartSummaryBackground = ({ width, height, left, noTick }) => {
	const triangleSize = 10;
	return (
		<SummaryBackgroundWrapper
			left={left}
			width={width || 0}
			height={height || 0}
		>
			{!noTick && (
				<SummaryTriangle size={triangleSize} top={height / 2 - triangleSize} />
			)}
		</SummaryBackgroundWrapper>
	);
};

export const WaterfallChart = props => {
	const {
		categories,
		categoryLevel,
		chartType,
		chartWidth,
		currencySymbol,
		hideDecimals,
		hideIndex,
		hideYAxisLabelValue,
		isWaterfall,
		isRoot,
		onItemClick,
		selectedIndex,
		setChartRef,
		series,
	} = props;
	const [highlightBlockHeight, setHighlightBlockHeight] = useState(0);
	const [highlightBlockWidth, setHighlightBlockWidth] = useState(0);
	const [highlightedIndex, setHighlightedIndex] = useState(null);
	const [menuOpenIndex, setMenuOpenIndex] = React.useState(null);
	const chartRef = React.useRef(null);
	const wrapperRef = React.useRef(null);
	const chart = chartRef.current && chartRef.current.chart;
	const type = chartType
		? CHART_TYPE_MAP[chartType] || chartType
		: CHART_TYPE_MAP[CHART_TYPES.BAR];

	const clearHighlighted = useCallback(
		() => {
			if (menuOpenIndex === null) {
				setHighlightedIndex(null);
			}
		},
		[menuOpenIndex]
	);

	const setHighlighted = useCallback(
		category => {
			let index = categories.indexOf(category);
			if (isWaterfall) {
				const list = [...categories].reverse();
				list.some((cat, i) => {
					if (i !== 0 && cat === category) {
						index = i;
						return true;
					}
					return false;
				});
			}
			if (category === 'Others') {
				setHighlightedIndex(null);
			} else if (menuOpenIndex === null) {
				setHighlightedIndex(index);
			}
		},
		[categories, isWaterfall, menuOpenIndex]
	);
	const onBarClick = useCallback(
		category => {
			const index = categories.indexOf(category);
			onItemClick(index, category);
		},
		[categories, onItemClick]
	);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	React.useLayoutEffect(() => {
		if (chartRef.current && chartRef.current.chart) {
			setChartRef && setChartRef(chartRef);

			setHighlightBlockHeight(chartRef.current.chart.plotHeight);
		}
	});

	// eslint-disable-next-line react-hooks/exhaustive-deps
	React.useLayoutEffect(() => {
		if (chartRef.current && chartRef.current.chart) {
			setHighlightBlockHeight(chartRef.current.chart.plotHeight);
		}
	});
	const svgElementClassName = 'details-chart';

	const spendFormatter = (value, withZero) => {
		return value || withZero
			? getFormattedSpendWithSymbol(
					value,
					currencySymbol,
					hideDecimals
			  ).toUpperCase()
			: '-';
	};

	const chartOptions = {
		chart: {
			type,
			spacingTop: 0,
			backgroundColor: 'rgba(0,0,0,0)',
			width: chartWidth || null,
			style: { fontFamily: 'Roboto' },
			marginTop: 0,
			spacingBottom: 40,
			height: 500,
			animation: { duration: 200 },
			events: {
				load: function({ target }) {
					target.container.style.overflow = 'visible';
					target.container.firstChild.classList.add(svgElementClassName);
					if (chartType === CHART_TYPES.WATERFALL) {
						applyWaterfallLabelsFixes({
							chart,
							type: chartType,
							categoryLevel,
							series,
						});
					}
				},
				redraw: function() {
					if (!chartRef.current || !chartRef.current.chart) {
						return;
					}

					const chart = chartRef.current.chart;

					if (highlightBlockHeight !== chart.plotHeight) {
						setHighlightBlockHeight(chart.plotHeight);
					}
					if (highlightBlockWidth !== chart.plotWidth) {
						setHighlightBlockWidth(chart.plotWidth);
					}
					if (chartType === CHART_TYPES.WATERFALL) {
						applyWaterfallLabelsFixes({
							chart,
							type: chartType,
							categoryLevel,
							series,
						});
					}
				},
			},
		},
		loading: {
			hideDuration: 500,
			showDuration: 500,
			labelStyle: {
				fontWeight: 'normal',
				letterSpacing: '0.3px',
			},
		},
		title: {
			text: null,
		},
		xAxis: {
			reversed: isWaterfall,
			categories,
			labels: {
				autoRotation: undefined,
				style: {
					color: colors.grey7,
					fontSize: '11px',
					letterSpacing: '0.3px',
					fontWeight: 500,
				},
			},
		},
		yAxis: {
			enabled: false,
			title: {
				text: '',
			},
			gridLineWidth: 0,
			labels: {
				enabled: false,
			},
			stackLabels: {
				enabled: true,
				align: 'center',
				formatter: function(e) {
					return Math.round(this.total * 100) / 100 !==
						Math.round(hideYAxisLabelValue * 100) / 100
						? spendFormatter(this.total, true)
						: '';
				},
			},
			lineColor: 'black',
			lineWidth: 0,
			min: 0,
			plotLines: [
				{
					value: 0,
					width: 1,
					color: colors.black,
					zIndex: 10,
				},
			],
		},
		legend: {
			enabled: series.length > 1,
			symbolRadius: 1,
			symbolHeight: 16,
			useHTML: true,
			itemMarginBottom: 12,
			labelFormatter: function() {
				return makeUnknownFromEmpty(this.name);
			},
			itemStyle: {
				color: colors.grey8,
				cursor: 'pointer',
				fontSize: '11px',
				fontWeight: 'normal',
				textOverflow: 'ellipsis',
			},
		},
		credits: { enabled: false },
		exporting: { enabled: false },
		series,
		tooltip: {
			enabled: menuOpenIndex === null,
			shared: true,
			distance: 56,
			useHTML: true,
			backgroundColor: 'rgba(255,255,255, 0.9)',
			borderWidth: 0,
			outside: true,
			borderRadius: 2,
			formatter: function() {
				const { points } = this;

				return series.length > 1
					? renderToString(
							<TooltipWrapper>
								<TooltipGrid>
									{points.map(pointData => (
										<React.Fragment key={pointData.point.name}>
											<ColorWrapper>
												<ColorSymbol color={pointData.color} />
											</ColorWrapper>
											<TooltipRowTitle>{pointData.point.name}</TooltipRowTitle>
											<TooltipRowValue>
												{spendFormatter(pointData.y)}
											</TooltipRowValue>
										</React.Fragment>
									))}
								</TooltipGrid>
							</TooltipWrapper>
					  )
					: renderToString(
							<TooltipWrapper>
								<TooltipGrid>
									<ColorWrapper>
										<ColorSymbol color={points[0].color} />
									</ColorWrapper>
									<TooltipRowValue>
										{spendFormatter(points[0].y)}
									</TooltipRowValue>
								</TooltipGrid>
							</TooltipWrapper>
					  );
			},
		},
		plotOptions: {
			waterfall: {
				stacking: 'normal',
				borderWidth: 0,
				dataLabels: {
					enabled: false,
				},
				events: {
					mouseOut: clearHighlighted,
				},
				point: {
					events: {
						mouseOver: function(e) {
							let isHoverable = true;
							if (isWaterfall && this.index === categories.length - 1) {
								isHoverable = false;
							}
							if (isHoverable) {
								setHighlighted(this.category);
							}
						},
						click: function(e) {
							onBarClick(this.category);
							setHighlighted(null);
						},
					},
				},
			},
			column:
				chartType === CHART_TYPES.BAR ||
				chartType === CHART_TYPES.STACKED ||
				chartType === CHART_TYPES.STACKED100
					? {
							stacking:
								chartType === CHART_TYPES.STACKED100 ? 'percent' : 'normal',
							dataLabels: {
								enabled: false,
							},
							events: {
								mouseOut: clearHighlighted,
							},
							point: {
								events: {
									mouseOver: function(e) {
										setHighlighted(this.category);
									},
									click: function(e) {
										onBarClick(this.category);
										setHighlighted(null);
									},
								},
							},
					  }
					: {},
			series: {
				animation: false,
				stacking: 'normal',
				pointPadding: 0,
				afterAnimate: function() {
					if (!chartRef.current || !chartRef.current.chart) {
						return;
					}

					setTimeout(() => {
						if (!chart || !chart.container || !chart.container.classList) {
							return;
						}
						if (chartType === CHART_TYPES.WATERFALL) {
							applyWaterfallLabelsFixes({
								chart,
								type: chartType,
								categoryLevel,
								series,
							});
						}

						chart.container.classList.remove('highcharts-container-loading');
					}, 1000);
				},
			},
		},
	};
	return (
		<Wrapper innerRef={wrapperRef} svgElementClassName={svgElementClassName}>
			{chart &&
				isWaterfall && (
					<ChartSummaryBackground
						left={chart.plotLeft}
						height={highlightBlockHeight + 40}
						width={
							(isRoot ? 2 * highlightBlockWidth : 1 * highlightBlockWidth) /
							categories.length
						}
					/>
				)}
			{chart &&
				selectedIndex && (
					<ChartSummaryBackground
						left={
							chart.plotLeft +
							selectedIndex * (highlightBlockWidth / categories.length)
						}
						height={highlightBlockHeight + 40}
						width={highlightBlockWidth / categories.length}
						noTick
					/>
				)}
			{chart && (
				<HighlightBlock
					{...props}
					highlightedIndex={highlightedIndex}
					isWaterfall={isWaterfall}
					left={chart.plotLeft}
					width={highlightBlockWidth}
					height={highlightBlockHeight + 40}
					hideIndex={hideIndex}
					onOut={clearHighlighted}
					menuOpenIndex={menuOpenIndex}
					setMenuOpenIndex={setMenuOpenIndex}
					onItemClick={onItemClick}
				/>
			)}

			<ChartWrapper
				onClick={e => {
					if (highlightedIndex) {
						e.preventDefault();
						e.stopPropagation();
						onItemClick(highlightedIndex);
					}
				}}
			>
				<HighchartsReact
					highcharts={Highcharts}
					options={chartOptions}
					ref={chartRef}
				/>
			</ChartWrapper>
		</Wrapper>
	);
};

const applyWaterfallLabelsFixes = ({
	chart,
	type,
	categoryLevel,
	series: dataSeries,
}) => {
	if (!chart || !chart.series) {
		return;
	}

	const yLabelsPositions = [];
	const seriesLength = chart.series.length;
	// position of the labels

	chart.series.forEach((series, i) => {
		// onAnimate triggers multiple times, sometimes with series.points as null
		if (!series.points) {
			return;
		}
		series.points.forEach((point, j) => {
			const dataLabel = point.dataLabel;

			if (!dataLabel) return;

			const dataValue = getDataValueByIndex(dataSeries, i, j);
			const isLastSeries = i === chart.series.length - 1;

			if (!yLabelsPositions[j] && (dataValue > 0 || isLastSeries)) {
				yLabelsPositions[j] = dataLabel.y;
			}
		});
	});

	chart.series.forEach((series, i) => {
		// onAnimate triggers multiple times, sometimes with series.points as null
		if (!series.points) {
			return;
		}
		series.points.forEach((point, j) => {
			const dataLabel = point.dataLabel;
			if (!dataLabel) return;

			const y =
				yLabelsPositions[j] - STACKED_LABEL_HEIGHT * (seriesLength - 1 - i);

			dataLabel.translate(dataLabel.x, y);
		});

		// remove dashed line from waterfall chart
		if (type === CHART_TYPES.WATERFALL && series && series.getCrispPath) {
			const pathArray = series.getCrispPath();

			if (categoryLevel === 0) {
				series.graph.attr({
					d: [...pathArray.slice(0, -6), ...pathArray.slice(-2)],
				});
			} else {
				series.graph.attr({
					d: pathArray.slice(0, -4),
				});
			}
		}
	});
};

const getDataValueByIndex = (dataSeries, i, j) => {
	const series = dataSeries[i];
	let dataValue = 0;

	if (series && series.data) {
		dataValue = series.data[j];

		// check if dataValue includes pattern together with value
		if (dataValue && (dataValue.value || dataValue.value === 0)) {
			dataValue = dataValue.value;
		}
	}

	return dataValue;
};

export default WaterfallChart;
